import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { GlassCard } from "@/components/GlassCard";
import { Button } from "@/components/ui/button";
import { ESignatureModal } from "@/components/ESignatureModal";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ListOrdered,
  Play,
  Volume2,
} from "lucide-react";

/**
 * Full-app execute experience: /sops/:id/execute
 * Embed mode uses EmbedExecute.jsx with reduced chrome.
 */
export default function ExecuteSOP({ embed = false }) {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [sop, setSop] = useState(null);
  const [steps, setSteps] = useState([]);
  const [execution, setExecution] = useState(null);
  const [idx, setIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [done, setDone] = useState(false);
  const locale = params.get("lang") || params.get("locale") || "en";

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const start = await api.post("/v1/wi/executions", {
          sop_id: id,
          context_type: params.get("context_type") || undefined,
          context_id: params.get("context_id") || undefined,
          work_order: params.get("work_order") || undefined,
          part_number: params.get("part") || params.get("part_number") || undefined,
          station_id: params.get("station") || params.get("station_id") || undefined,
          locale,
          device_id: params.get("device_id") || undefined,
        });
        setSop(start.data.sop);
        setSteps(start.data.steps || []);
        setExecution(start.data.execution);
        setIdx(start.data.execution?.current_step_index || 0);
      } catch (e) {
        setErr(formatApiError(e?.response?.data?.detail) || "Could not start execution");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const step = steps[idx];
  const completedIds = useMemo(
    () => new Set((execution?.steps_completed || []).map((s) => s.step_id)),
    [execution]
  );
  const progress = steps.length ? Math.round((completedIds.size / steps.length) * 100) : 0;

  const playTts = () => {
    const audio = step?.media?.audio?.[locale] || step?.media?.audio?.en;
    if (audio?.url) {
      const a = new Audio(audio.url);
      a.play().catch(() => {});
      return;
    }
    const text = audio?.text || `${step?.title || ""}. ${step?.body || ""}`;
    if (!text || !window.speechSynthesis) return;
    const u = new SpeechSynthesisUtterance(text);
    u.lang = locale === "hi" ? "hi-IN" : locale === "de" ? "de-DE" : "en-US";
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  };

  const completeCurrent = async (signatureName) => {
    if (!execution?.id || !step) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/v1/wi/executions/${execution.id}/steps/${step.id}/complete`,
        { signature_name: signatureName || undefined }
      );
      const nextCompleted = [
        ...(execution.steps_completed || []),
        { step_id: step.id, completed_at: new Date().toISOString() },
      ];
      setExecution({ ...execution, steps_completed: nextCompleted });
      if (data.finished) {
        setDone(true);
      } else if (idx < steps.length - 1) {
        setIdx(idx + 1);
      }
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Failed to complete step");
    } finally {
      setBusy(false);
      setSignOpen(false);
    }
  };

  const onCompleteClick = () => {
    if (step?.requires_signoff) setSignOpen(true);
    else completeCurrent();
  };

  if (loading) {
    return (
      <div className={`${embed ? "min-h-screen p-6" : ""} text-[#9AA4B2]`} data-testid="execute-loading">
        Starting work instruction…
      </div>
    );
  }

  if (err && !sop) {
    return (
      <div className="p-6" data-testid="execute-error">
        <p className="text-rose-300">{err}</p>
        {!embed && (
          <Button className="mt-4 btn-ghost-glass rounded-full" onClick={() => navigate(`/sops/${id}`)}>
            Back to SOP
          </Button>
        )}
      </div>
    );
  }

  if (done) {
    return (
      <div className={`${embed ? "min-h-screen flex items-center justify-center p-6 bg-[#05070D]" : ""}`} data-testid="execute-done">
        <GlassCard className="p-8 max-w-md text-center">
          <CheckCircle2 className="mx-auto text-emerald-400" size={40} />
          <h1 className="mt-4 font-display text-2xl text-white">Procedure complete</h1>
          <p className="mt-2 text-sm text-[#9AA4B2]">{sop?.title} — all steps signed off.</p>
          {!embed && (
            <Button className="mt-6 btn-primary-glow rounded-full" onClick={() => navigate(`/sops/${id}`)}>
              Back to SOP
            </Button>
          )}
        </GlassCard>
      </div>
    );
  }

  return (
    <div
      className={embed ? "min-h-screen bg-[#05070D] text-white p-4 sm:p-6" : "space-y-6"}
      data-testid="execute-page"
    >
      {!embed && (
        <button
          className="text-sm text-[#9AA4B2] hover:text-white inline-flex items-center gap-1.5"
          onClick={() => navigate(`/sops/${id}`)}
        >
          <ArrowLeft size={14} /> Back
        </button>
      )}

      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-cyan-300 inline-flex items-center gap-1.5">
            <Play size={12} /> Execute {embed ? "· embed" : ""}
          </div>
          <h1 className="mt-1 font-display text-2xl sm:text-3xl text-white">{sop?.title}</h1>
          <p className="text-sm text-[#5B6472] mt-1">
            v{sop?.current_version_number} · Step {Math.min(idx + 1, steps.length)} of {steps.length}
          </p>
        </div>
        <div className="text-right">
          <div className="text-xs text-[#9AA4B2]">Progress</div>
          <div className="font-display text-2xl text-white">{progress}%</div>
        </div>
      </header>

      <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-[#2563EB] to-[#06B6D4] transition-all"
          style={{ width: `${progress}%` }}
        />
      </div>

      {err && <div className="text-sm text-rose-300">{err}</div>}

      <div className="grid lg:grid-cols-[1fr_280px] gap-4">
        <GlassCard className="p-6 sm:p-8 min-h-[320px]">
          {!step ? (
            <div className="text-[#5B6472]">No structured steps on this SOP. Add steps in the editor or import a PDF.</div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-3">
                <h2 className="font-display text-xl sm:text-2xl text-white leading-snug">{step.title}</h2>
                <Button
                  type="button"
                  className="btn-ghost-glass rounded-full shrink-0"
                  onClick={playTts}
                  data-testid="execute-tts"
                >
                  <Volume2 size={16} />
                </Button>
              </div>
              <p className="mt-4 text-[#cbd5e1] leading-relaxed whitespace-pre-wrap">{step.body || "—"}</p>

              {(step.tools?.length > 0 || step.safety?.length > 0) && (
                <div className="mt-6 grid sm:grid-cols-2 gap-3">
                  {step.tools?.length > 0 && (
                    <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-3">
                      <div className="text-[10px] uppercase tracking-widest text-[#9AA4B2]">Tools</div>
                      <ul className="mt-1 text-sm text-white list-disc pl-4">
                        {step.tools.map((t) => (
                          <li key={t}>{t}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {step.safety?.length > 0 && (
                    <div className="rounded-2xl bg-rose-500/10 border border-rose-400/20 p-3">
                      <div className="text-[10px] uppercase tracking-widest text-rose-300">Safety</div>
                      <ul className="mt-1 text-sm text-rose-100 list-disc pl-4">
                        {step.safety.map((t) => (
                          <li key={t}>{t}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {(step.media?.photo_url || step.media?.illustration_url || step.media?.page_image) && (
                <div className="mt-6 relative rounded-2xl overflow-hidden border border-white/10 bg-black/30">
                  <img
                    src={step.media.photo_url || step.media.page_image || step.media.illustration_url}
                    alt=""
                    className="w-full max-h-80 object-contain"
                  />
                  {(step.media.annotations || []).map((a, i) => (
                    <div
                      key={i}
                      className="absolute border-2 border-cyan-400/80 bg-cyan-400/10 text-[10px] text-cyan-100 px-1"
                      style={{
                        left: `${a.x || 0}%`,
                        top: `${a.y || 0}%`,
                        width: a.w ? `${a.w}%` : undefined,
                        height: a.h ? `${a.h}%` : undefined,
                      }}
                    >
                      {a.label}
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
                <Button
                  className="btn-ghost-glass rounded-full"
                  disabled={idx === 0 || busy}
                  onClick={() => setIdx((v) => Math.max(0, v - 1))}
                >
                  <ArrowLeft size={14} className="mr-1" /> Previous
                </Button>
                <Button
                  className="btn-primary-glow rounded-full"
                  disabled={busy || completedIds.has(step.id)}
                  onClick={onCompleteClick}
                  data-testid="execute-complete-step"
                >
                  {completedIds.has(step.id) ? "Completed" : "Complete step"}
                  <ArrowRight size={14} className="ml-1" />
                </Button>
              </div>
            </>
          )}
        </GlassCard>

        <GlassCard className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#9AA4B2] flex items-center gap-1.5 mb-3">
            <ListOrdered size={12} /> Steps
          </div>
          <ul className="space-y-1.5 max-h-[420px] overflow-auto">
            {steps.map((s, i) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => setIdx(i)}
                  className={`w-full text-left px-3 py-2 rounded-xl text-sm transition ${
                    i === idx
                      ? "bg-white/[0.08] text-white border border-white/15"
                      : "text-[#9AA4B2] hover:bg-white/[0.04]"
                  }`}
                >
                  <span className="text-[10px] text-[#5B6472] mr-2">{i + 1}</span>
                  {completedIds.has(s.id) && <CheckCircle2 size={12} className="inline text-emerald-400 mr-1" />}
                  {s.title}
                </button>
              </li>
            ))}
          </ul>
        </GlassCard>
      </div>

      <ESignatureModal
        open={signOpen}
        onOpenChange={setSignOpen}
        title="Sign off this step"
        action="acknowledge"
        onConfirm={async (name) => {
          await completeCurrent(name);
        }}
      />
    </div>
  );
}
